import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { SelectionEligibility } from '@/lib/views/actions'

/** «Выбрать все по фильтру» checks each piece only up to this population; a larger one stays a filter selection. */
export const ELIGIBILITY_RESOLVE_LIMIT = 1000
const RESOLVE_PAGE_SIZE = 200

export interface SelectionRefusal {
  count: number
  reasons: readonly { reason: string; count: number }[]
}

export type ResolvedSelection =
  | { kind: 'filter' }
  | { kind: 'eligible'; ids: string[]; refused: SelectionRefusal }

/**
 * Read the whole filtered population and keep the pieces the preset's actions
 * accept, so «Выбрано» is what is drawn selected and the stats and the actions
 * receive exactly those. When every piece is eligible, or the population cannot
 * be read whole, the selection stays the server-resolved filter.
 */
export async function resolveEligibleSelection({ total, eligibility, readPage }: {
  total: number
  eligibility: (row: InventoryListItem) => SelectionEligibility
  readPage: (offset: number, limit: number) => Promise<{ total: number; items: readonly InventoryListItem[] }>
}): Promise<ResolvedSelection> {
  if (total <= 0 || total > ELIGIBILITY_RESOLVE_LIMIT) return { kind: 'filter' }
  const rows = new Map<string, InventoryListItem>()
  for (let offset = 0; offset < total; offset += RESOLVE_PAGE_SIZE) {
    const page = await readPage(offset, RESOLVE_PAGE_SIZE)
    // The population moved while it was being read: the server-side filter is the only honest answer.
    if (page.total !== total) return { kind: 'filter' }
    for (const row of page.items) if (row.rowGrain === 'piece') rows.set(row.productId, row)
  }
  if (rows.size !== total) return { kind: 'filter' }
  const ids: string[] = []
  const reasons = new Map<string, number>()
  for (const row of rows.values()) {
    const verdict = eligibility(row)
    if (verdict.ok) ids.push(row.productId)
    else reasons.set(verdict.reason, (reasons.get(verdict.reason) ?? 0) + 1)
  }
  if (reasons.size === 0) return { kind: 'filter' }
  return {
    kind: 'eligible',
    ids,
    refused: { count: total - ids.length, reasons: [...reasons].map(([reason, count]) => ({ reason, count })) },
  }
}
